/**
 * Sidebar (E58)
 *
 * - Dashboard (oben, fest)
 * - Kategorien als Menüpunkte (dynamisch aus ProgrammeRegistry, permission-gefiltert)
 * - Historie + Einstellungen (unten, fest)
 * - Collapsible
 * - CI: bg-brand-black, Active = bg-brand-yellow text-brand-black
 */

import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useProgrammeStore } from '../stores/programmeStore';
import { formatCategoryLabel } from '../lib/programmeLabel';
import { AnyPermissionGate } from './PermissionGate';

const SETTINGS_PERMISSIONS = [
  'manage:users',
  'manage:integrations',
  'manage:schedules',
  'manage:system',
  'manage:access',
  'manage:roles',
];
import { api } from '../services/api';
import { getEnvColor, useEnvironmentStatus } from './EnvironmentBanner';

function getIcon(name: string, className?: string) {
  const Icon = (LucideIcons as any)[name];
  if (!Icon) return <LucideIcons.Box className={className || 'w-5 h-5'} />;
  return <Icon className={className || 'w-5 h-5'} />;
}

const linkBase =
  'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors';
const linkActive = 'bg-brand-yellow text-brand-black';
const linkInactive = 'text-white/70 hover:text-white hover:bg-white/10';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  /** Unter 1024 px: ist die Schublade ausgefahren? Darüber ohne Bedeutung. */
  mobilOffen?: boolean;
  /** Schließt die Schublade — beim Tippen auf einen Punkt oder das X. */
  onSchliessen?: () => void;
}

interface VersionInfo {
  backend: {
    version: string;
    gitCommit: string;
    gitCommitFull: string;
    gitBranch: string;
    gitMessage: string;
    buildTime: string;
  };
  frontend: {
    version: string;
  };
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ', ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
}

export function Sidebar({ collapsed, onToggle, mobilOffen = false, onSchliessen }: SidebarProps) {
  const { isVisible: bannerSichtbar } = useEnvironmentStatus();
  const { user, logout } = useAuthStore();
  const { categories } = useProgrammeStore();
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [antragBadge, setAntragBadge] = useState(0);

  useEffect(() => {
    api.get('/system/version')
      .then((res) => setVersionInfo(res.data?.data ?? null))
      .catch(() => {/* bei Fehler nichts anzeigen */});
  }, []);

  // Offene-Anträge-Zähler — alle 60s aktualisieren
  useEffect(() => {
    let active = true;
    const load = () => {
      api.programmantraege.badge()
        .then((c) => { if (active) setAntragBadge(c); })
        .catch(() => {/* still */});
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { active = false; clearInterval(iv); };
  }, []);

  return (
    <aside
      className={`fixed left-0 bg-brand-black flex flex-col z-40 transition-transform
        duration-200 lg:transition-all
        w-64 ${collapsed ? 'lg:w-16' : 'lg:w-64'}
        ${mobilOffen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      style={{
        // Der Umgebungs-Banner ist `fixed top-0 h-6 z-50`. Eine Sidebar mit `top-0`
        // liegt darunter und ihr Kopf wird angeschnitten — genau das war auf dem Handy
        // zu sehen („Libero" halb verdeckt). Deshalb beginnt sie unter dem Banner und
        // ist entsprechend kürzer.
        top: bannerSichtbar ? '1.5rem' : 0,
        height: bannerSichtbar ? 'calc(100vh - 1.5rem)' : '100vh',
      }}
    >
      {/* Logo */}
      <div className="flex items-center h-14 px-4 border-b border-white/10 shrink-0">
        <span className="font-bold text-xl shrink-0" style={{ color: getEnvColor() }}>L</span>
        <span className={`text-white font-semibold text-lg ml-2 whitespace-nowrap
          ${collapsed ? 'lg:hidden' : ''}`}>
          Libero
        </span>
        {/* Nur auf dem Handy: Schublade wieder zu. */}
        <button
          type="button"
          onClick={onSchliessen}
          aria-label="Menü schließen"
          className="lg:hidden ml-auto p-2 -mr-2 text-white/60 hover:text-white
                     min-w-[44px] min-h-[44px] flex items-center justify-center"
        >
          <LucideIcons.X className="w-5 h-5" />
        </button>
      </div>

      {/* Top: Dashboard */}
      <nav className="px-2 pt-3 pb-1 shrink-0">
        <NavLink
          to="/"
          end
          className={({ isActive }) =>
            `${linkBase} ${isActive ? linkActive : linkInactive}`
          }
          title="Dashboard"
        >
          <LucideIcons.LayoutDashboard className="w-5 h-5 shrink-0" />
          <span className={collapsed ? 'lg:hidden' : ''}>Dashboard</span>
        </NavLink>
      </nav>

      <div className="mx-3 border-t border-white/10 my-1" />

      {/* Categories (E58: Kategorien als Menüpunkte, Klick → CategoryPage mit Karten) */}
      <nav className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
        {categories.map((cat) => (
          <NavLink
            key={cat.name}
            to={`/category/${cat.kategorieNr ?? encodeURIComponent(cat.name)}`}
            className={({ isActive }) =>
              `${linkBase} ${isActive ? linkActive : linkInactive}`
            }
            title={formatCategoryLabel(cat)}
          >
            {getIcon(cat.icon, 'w-5 h-5 shrink-0')}
            <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>{formatCategoryLabel(cat)}</span>
          </NavLink>
        ))}
      </nav>

      <div className="mx-3 border-t border-white/10 my-1" />

      {/* Bottom: Historie + Einstellungen */}
      <nav className="px-2 pb-2 pt-1 space-y-0.5 shrink-0">
        <NavLink
          to="/history"
          className={({ isActive }) =>
            `${linkBase} ${isActive ? linkActive : linkInactive}`
          }
          title="Historie"
        >
          <LucideIcons.History className="w-5 h-5 shrink-0" />
          <span className={collapsed ? 'lg:hidden' : ''}>Historie</span>
        </NavLink>

        <NavLink
          to="/artikel-datenbank"
          className={({ isActive }) =>
            `${linkBase} ${isActive ? linkActive : linkInactive}`
          }
          title="Artikel-Datenbank"
        >
          <LucideIcons.Database className="w-5 h-5 shrink-0" />
          <span className={collapsed ? 'lg:hidden' : ''}>Artikel-Datenbank</span>
        </NavLink>

        <NavLink
          to="/programmantraege"
          className={({ isActive }) =>
            `${linkBase} ${isActive ? linkActive : linkInactive}`
          }
          title="Programmanträge"
        >
          <LucideIcons.FilePlus className="w-5 h-5 shrink-0" />
          <span className={`flex-1 ${collapsed ? 'lg:hidden' : ''}`}>Programmanträge</span>
          {antragBadge > 0 && (
            <span
              className={`bg-brand-yellow text-brand-black text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ${collapsed ? 'lg:absolute lg:ml-6 lg:-mt-4' : ''}`}
              title={`${antragBadge} offene Anträge`}
            >
              {antragBadge}
            </span>
          )}
        </NavLink>

        <AnyPermissionGate permissions={SETTINGS_PERMISSIONS}>
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? linkActive : linkInactive}`
            }
            title="Einstellungen"
          >
            <LucideIcons.Settings className="w-5 h-5 shrink-0" />
            <span className={collapsed ? 'lg:hidden' : ''}>Einstellungen</span>
          </NavLink>
        </AnyPermissionGate>
      </nav>

      {/* Versionsanzeige */}
      {versionInfo && (
        <div
          className={`px-4 py-2 shrink-0 ${collapsed ? 'lg:hidden' : ''}`}
          title={[
            `Version: ${versionInfo.backend.version}`,
            `Commit: ${versionInfo.backend.gitCommitFull}`,
            `Branch: ${versionInfo.backend.gitBranch}`,
            `Message: ${versionInfo.backend.gitMessage}`,
          ].join('\n')}
        >
          <p className="text-[10px] text-white/30 leading-tight font-mono">
            v{versionInfo.backend.version} · {versionInfo.backend.gitCommit}
          </p>
          <p className="text-[10px] text-white/25 leading-tight truncate">
            {versionInfo.backend.gitBranch}
          </p>
          {versionInfo.backend.buildTime && (
            <p className="text-[10px] text-white/20 leading-tight">
              Aktualisiert: {formatDate(versionInfo.backend.buildTime)}
            </p>
          )}
        </div>
      )}

      {/* User display + collapse toggle */}
      <div className="border-t border-white/10 shrink-0">
        {user && (
          <div className={`px-4 py-2 flex items-center justify-between
            ${collapsed ? 'lg:justify-center' : ''}`}>
            <span className={`text-xs text-white/40 truncate ${collapsed ? 'lg:hidden' : ''}`}>
              {user.firstName} {user.lastName}
            </span>
            <button
              onClick={() => logout()}
              title="Abmelden"
              aria-label="Abmelden"
              className="text-white/40 hover:text-white transition-colors"
            >
              <LucideIcons.LogOut className="w-4 h-4" />
            </button>
          </div>
        )}
        <button
          onClick={onToggle}
          className="hidden lg:flex items-center justify-center w-full h-10 text-gray-400 hover:text-white transition-colors"
          title={collapsed ? 'Sidebar einblenden' : 'Sidebar ausblenden'}
        >
          {collapsed ? (
            <LucideIcons.ChevronRight className="w-4 h-4" />
          ) : (
            <LucideIcons.ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>
    </aside>
  );
}
